import classNames from "classnames"
import {kebabCase, startCase} from "lodash"
import moment from "moment"
import numeral from "numeral"
import React from "react"
import {useNavigate} from "react-router-dom"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {
	Bill,
	BillDetailInclude,
	BillFile,
	findBillFile,
	findVendor,
	getBill,
	getBillDeductionDate,
	getVendorName,
	Vendor,
} from "../../resources/bills"
import {ErrorDocument, OkDocument} from "../../resources/common"
import {Customer, getCustomerName} from "../../resources/customer"
import {AsyncResultRequestState} from "../../types/asyncResult"
import {DrawerHeader} from "../Drawer/Drawer"
import {
	KeyValueCard,
	KeyValueCardContainer,
	KeyValueCardKey,
	KeyValueCardPending,
	KeyValueCardValue,
} from "../KeyValueCard/KeyValueCard"
import {TagsViewerKeyValue} from "../Tags/TagsViewer"
import {CustomerCard} from "../Customers/CustomerCard"
import {BillFilePreview} from "./BillFilePreview"
import {DataTable, DataTableBody, DataTableCell, DataTableHead, DataTableRow} from "../DataTable/DataTable"

interface BillProps {
	billId: string
	billAsyncResult?: AsyncResultRequestState<OkDocument<Bill>, ErrorDocument>
	accessToken: string
	useDrawer: boolean
	customer?: Customer
	vendor?: Vendor
	billFile?: BillFile
	hideLineItems?: boolean
}

function BillPending() {
	return (
		<div className="bill-content">
			<KeyValueCardPending rows={6} />
			<KeyValueCardPending rows={6} />
		</div>
	)
}

function getCustomerHref(customer: Customer): string {
	return `/${customer.type === "individualCustomer" ? "individual" : "business"}/${customer.id}`
}

export function BillLineItemsCard({bill}: {bill: Bill}) {
	const {attributes} = bill
	if (!attributes.lineItems || attributes.lineItems.length === 0) return null
	return (
		<KeyValueCard title="Line Items">
			<DataTable isEmpty={false} className="bills-line-items-table" fullHeight={false} hideMinHeight clickable={false}>
				<DataTableHead>
					<DataTableRow>
						<DataTableCell>Description</DataTableCell>
						<DataTableCell>Quantity</DataTableCell>
						<DataTableCell>Unit Price</DataTableCell>
						<DataTableCell>Tax</DataTableCell>
						<DataTableCell>Amount</DataTableCell>
					</DataTableRow>
				</DataTableHead>
				<DataTableBody>
					{attributes.lineItems.map((lineItem, index) => (
						<DataTableRow key={index}>
							<DataTableCell>{lineItem.description}</DataTableCell>
							<DataTableCell>{lineItem.quantity}</DataTableCell>
							<DataTableCell>{numeral(lineItem.unitPrice / 100).format("$0,0.00")}</DataTableCell>
							<DataTableCell>{numeral(lineItem.tax / 100).format("$0,0.00")}</DataTableCell>
							<DataTableCell>{numeral(lineItem.totalAmount / 100).format("$0,0.00")}</DataTableCell>
						</DataTableRow>
					))}
				</DataTableBody>
			</DataTable>
		</KeyValueCard>
	)
}

function BillDetails({
	bill,
	customer,
	vendor,
	billFile,
	showStatus,
	hideLineItems,
}: {
	bill: Bill
	customer?: Customer
	vendor?: Vendor
	billFile?: BillFile
	showStatus: boolean
	hideLineItems?: boolean
}) {
	const navigate = useNavigate()
	const {attributes, relationships} = bill
	const customerId = relationships.customer?.data.id
	const vendorName = vendor ? getVendorName(vendor) : relationships.vendor?.data.id
	const customerName = customer ? getCustomerName(customer) : customerId
	const deductionDate = getBillDeductionDate(attributes.paymentMethod)

	return (
		<>
			<KeyValueCard title="Bill Details">
				<KeyValueCardContainer>
					<>
						<KeyValueCardKey>Invoice #</KeyValueCardKey>
						<KeyValueCardValue>{attributes.invoiceNumber}</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>Amount</KeyValueCardKey>
						<KeyValueCardValue>{numeral(attributes.total / 100).format("$0,0.00")}</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>Tax</KeyValueCardKey>
						<KeyValueCardValue>{numeral(attributes.tax / 100).format("$0,0.00")}</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>Date</KeyValueCardKey>
						<KeyValueCardValue>{moment(attributes.date).format("L")}</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>Due Date</KeyValueCardKey>
						<KeyValueCardValue>{moment(attributes.dueDate).format("L")}</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>Deduction Date</KeyValueCardKey>
						<KeyValueCardValue>{deductionDate ? moment(deductionDate).format("L") : "--"}</KeyValueCardValue>
					</>
					{showStatus && (
						<>
							<KeyValueCardKey>Status</KeyValueCardKey>
							<KeyValueCardValue>
								<div className={classNames("bills-status", kebabCase(attributes.status))}>
									{startCase(attributes.status)}
								</div>
							</KeyValueCardValue>
						</>
					)}
					<>
						<KeyValueCardKey>Customer</KeyValueCardKey>
						<KeyValueCardValue>
							{customer && customerName ? (
								<a
									className="link"
									href={getCustomerHref(customer)}
									onClick={(e) => {
										e.preventDefault()
										e.stopPropagation()
										navigate(getCustomerHref(customer))
									}}
								>
									{customerName}
								</a>
							) : (
								customerId
							)}
						</KeyValueCardValue>
					</>
					<>
						<KeyValueCardKey>Vendor</KeyValueCardKey>
						<KeyValueCardValue>
							{relationships.vendor?.data.id ? (
								<a
									className="link"
									href={`/vendors/${relationships.vendor.data.id}`}
									onClick={(e) => {
										e.preventDefault()
										e.stopPropagation()
										navigate(`/vendors/${relationships.vendor.data.id}`)
									}}
								>
									{vendorName}
								</a>
							) : (
								vendorName
							)}
						</KeyValueCardValue>
					</>
				</KeyValueCardContainer>
				<KeyValueCardContainer className="tags-content">
					{attributes.tags && Object.keys(attributes.tags).length > 0 && <TagsViewerKeyValue tags={attributes.tags} />}
				</KeyValueCardContainer>
			</KeyValueCard>

			<BillFilePreview billFile={billFile} />

			{!hideLineItems && <BillLineItemsCard bill={bill} />}
		</>
	)
}

function BillInternal({
	bill,
	useDrawer,
	customer,
	vendor,
	billFile,
	hideLineItems,
}: {
	bill: Bill
	useDrawer: boolean
	customer?: Customer
	vendor?: Vendor
	billFile?: BillFile
	hideLineItems?: boolean
}) {
	const status = bill.attributes.status

	return (
		<>
			{useDrawer && (
				<DrawerHeader title={`Bill #${bill.id}`}>
					<div className={classNames("bills-status", kebabCase(status))}>{startCase(status)}</div>
				</DrawerHeader>
			)}
			<div className="bill-content">
				<BillDetails
					bill={bill}
					customer={customer}
					vendor={vendor}
					billFile={billFile}
					showStatus={useDrawer}
					hideLineItems={hideLineItems}
				/>
				{customer && <CustomerCard customer={customer} customerId={customer.id} />}
			</div>
		</>
	)
}

export function BillComponent({
	billId,
	billAsyncResult,
	accessToken,
	useDrawer,
	customer,
	vendor,
	billFile,
	hideLineItems,
}: BillProps) {
	const billAsyncResultReq = billAsyncResult || useAsyncResult(() => getBill(accessToken, billId, BillDetailInclude))

	return (
		<AsyncResultComponent asyncResult={billAsyncResultReq} pendingComponent={<BillPending />}>
			{({value: bill}) => {
				const billCustomerId = bill.data.relationships.customer?.data.id
				const billVendorId = bill.data.relationships.vendor?.data.id
				const resolvedCustomer =
					customer ??
					(bill.included?.find(
						(r) =>
							(r.type === "individualCustomer" || r.type === "businessCustomer") && r.id === billCustomerId?.toString()
					) as Customer | undefined)
				const resolvedVendor = vendor ?? (billVendorId ? findVendor(billVendorId, bill.included) : undefined)
				const resolvedBillFile = billFile ?? findBillFile(bill.data, bill.included)

				return (
					<BillInternal
						useDrawer={useDrawer}
						bill={bill.data}
						customer={resolvedCustomer}
						vendor={resolvedVendor}
						billFile={resolvedBillFile}
						hideLineItems={hideLineItems}
					/>
				)
			}}
		</AsyncResultComponent>
	)
}
